import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Certs, Listings } from '../api/broker';
import CertScanner from '../components/CertScanner';
import {
    CertImage,
    PriceChartingGradesCell,
    PriceChartingProductCell,
    formatMoney,
    suggestedPrice,
    certMatchesQuery,
} from '../components/CertCells';
import { useToast } from '../components/Toast';
import AutoPriceInfo from '../components/AutoPriceInfo';

const PAGE_SIZE = 50;
// The whole catalog is only a few hundred certs, so a broker's set fits in one
// fetch. We load it all and filter/paginate on the client — the API has no
// text search (only exact cert #), so client-side is the only way to filter
// by name/set across the full set instead of just the current page.
const LOAD_LIMIT = 2000;

const CertsTab = ({ brokerEmail, filters, goTo, onZoom, onCountChange }) => {
    const toast = useToast();
    const [certs, setCerts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rowPrices, setRowPrices] = useState({});
    const [rowAutoPrice, setRowAutoPrice] = useState({});
    const [busyRows, setBusyRows] = useState({});
    const [newCertNumber, setNewCertNumber] = useState('');
    const [addBusy, setAddBusy] = useState(false);
    // `query` is the live input; `appliedQuery` is what actually filters — it
    // only updates when the broker hits Filter (or Enter), so results don't
    // churn on every keystroke.
    const [query, setQuery] = useState(filters.q || '');
    const [appliedQuery, setAppliedQuery] = useState(filters.q || '');
    const [page, setPage] = useState(1);
    const [scanning, setScanning] = useState(false);
    // Rows just added (manually or by the scanner) — highlighted briefly.
    const [freshRows, setFreshRows] = useState({});
    // Every cert the broker owns, listed or not, by cert number — the table hides
    // listed ones, but the scanner still needs them (duplicates, listing panel).
    const ownedRef = useRef(new Map());

    useEffect(() => {
        setQuery(filters.q || '');
        setAppliedQuery(filters.q || '');
    }, [filters.q]);

    const setRowBusy = (cert_number, v) =>
        setBusyRows((prev) => ({ ...prev, [cert_number]: v }));

    const removeRow = (cert_number) =>
        setCerts((prev) => prev.filter((c) => c.cert_number !== cert_number));

    const load = useCallback(async () => {
        if (!brokerEmail) {
            setCerts([]);
            onCountChange?.(0);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const result = await Certs.listMine({ count: LOAD_LIMIT });
            ownedRef.current = new Map((result.certs || []).map((c) => [String(c.cert_number), c]));
            // Only show certs that aren't already listed actively — same as previous behaviour.
            const filtered = (result.certs || []).filter((c) =>
                !c.listings ||
                c.listings.length === 0 ||
                c.listings.every((l) => !l.is_active)
            );
            setCerts(filtered);
            onCountChange?.(filtered.length);

            const prices = {};
            filtered.forEach((c) => {
                prices[c.cert_number] = suggestedPrice(c);
            });
            setRowPrices(prices);
        } catch (e) {
            setError(e.message);
            setCerts([]);
            onCountChange?.(0);
        } finally {
            setLoading(false);
        }
    }, [brokerEmail, onCountChange]);

    useEffect(() => { load(); }, [load]);

    // Full text filter over the broker's whole set (name / cert # / set / …).
    const filteredCerts = useMemo(() => {
        const q = appliedQuery.trim();
        return q ? certs.filter((c) => certMatchesQuery(c, q)) : certs;
    }, [certs, appliedQuery]);

    const totalPages = Math.max(1, Math.ceil(filteredCerts.length / PAGE_SIZE));

    // Keep the page in range as the applied filter or the set size changes.
    useEffect(() => {
        setPage((p) => Math.min(Math.max(1, p), Math.max(1, Math.ceil(filteredCerts.length / PAGE_SIZE))));
    }, [filteredCerts.length]);

    const pageCerts = useMemo(
        () => filteredCerts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [filteredCerts, page]
    );

    const applyFilter = () => {
        const q = query.trim();
        setAppliedQuery(q);
        setPage(1);
        goTo({ tab: 'certs', q: q || undefined });
    };

    const clearFilter = () => {
        setQuery('');
        setAppliedQuery('');
        setPage(1);
        goTo({ tab: 'certs' });
    };

    const onPriceChange = (cert_number, price) =>
        setRowPrices((prev) => ({ ...prev, [cert_number]: price }));

    const onAutoPriceChange = (cert_number, autoPrice) =>
        setRowAutoPrice((prev) => ({ ...prev, [cert_number]: autoPrice }));

    // POST /listings, then drop the cert from the (unlisted-only) table. Shared by
    // the table's List button and the scanner's listing panel; throws on failure.
    const listCert = useCallback(async ({ cert, price, autoPrice }) => {
        const result = await Listings.create({
            sellerEmail: brokerEmail,
            certNumber: cert.cert_number,
            price,
            autoPrice: !!autoPrice,
        });
        const created = result?.listings?.[0]
            || { is_active: true, price: parseFloat(price), auto_price: !!autoPrice };
        const key = String(cert.cert_number);
        const owned = ownedRef.current.get(key);
        if (owned) {
            ownedRef.current.set(key, { ...owned, listings: [...(owned.listings || []), created] });
        }
        setCerts((prev) => {
            const next = prev.filter((c) => String(c.cert_number) !== key);
            if (next.length !== prev.length) onCountChange?.(next.length);
            return next;
        });
        return created;
    }, [brokerEmail, onCountChange]);

    // PATCH an existing listing's price / auto-price (scanner edit panel).
    const updateListing = useCallback(async ({ cert, listing, price, autoPrice }) => {
        const result = await Listings.update({ listingId: listing.listing_id, price, autoPrice: !!autoPrice });
        const updated = { ...listing, price: parseFloat(price), auto_price: !!autoPrice, ...(result || {}) };
        const key = String(cert.cert_number);
        const owned = ownedRef.current.get(key);
        if (owned) {
            ownedRef.current.set(key, {
                ...owned,
                listings: (owned.listings || []).map((l) => (l.listing_id === listing.listing_id ? updated : l)),
            });
        }
        return updated;
    }, []);

    const handleList = async (cert) => {
        const price = rowPrices[cert.cert_number];
        if (!price || Number.isNaN(parseFloat(price))) {
            toast.error(`Enter a price before listing #${cert.cert_number}`);
            return;
        }
        setRowBusy(cert.cert_number, true);
        try {
            await listCert({ cert, price, autoPrice: rowAutoPrice[cert.cert_number] });
            toast.success(`Listed #${cert.cert_number} for ${formatMoney(price)}`);
        } catch (e) {
            toast.error(`Failed to list #${cert.cert_number}: ${e.message}`);
            setRowBusy(cert.cert_number, false);
        }
    };

    const handleHide = async (cert) => {
        if (!window.confirm(`Hide cert #${cert.cert_number}? This deactivates it.`)) return;
        setRowBusy(cert.cert_number, true);
        try {
            await Certs.deactivate(cert.cert_number);
            toast.success(`Hid #${cert.cert_number}`);
            removeRow(cert.cert_number);
        } catch (e) {
            toast.error(`Failed to hide #${cert.cert_number}: ${e.message}`);
            setRowBusy(cert.cert_number, false);
        }
    };

    const isOwned = useCallback((certNumber) => ownedRef.current.has(String(certNumber)), []);
    const getCert = useCallback((certNumber) => ownedRef.current.get(String(certNumber)) || null, []);

    // POST /cert and put the new cert at the top of the list. Shared by the
    // manual input and the scanner; throws so each caller can report failure.
    const addCert = useCallback(async (certNumber) => {
        const result = await Certs.create({ certNumber });
        const created = result?.cert || result || {};
        const newCert = { cert_number: certNumber, ...created };
        ownedRef.current.set(String(certNumber), newCert);
        setCerts((prev) => {
            const next = [newCert, ...prev.filter((c) => String(c.cert_number) !== String(certNumber))];
            onCountChange?.(next.length);
            return next;
        });
        setRowPrices((prev) => ({
            ...prev,
            [certNumber]: suggestedPrice(newCert) || '',
        }));
        setFreshRows((prev) => ({ ...prev, [certNumber]: true }));
        setTimeout(() => {
            setFreshRows((prev) => {
                const { [certNumber]: _, ...rest } = prev;
                return rest;
            });
        }, 2500);
        setPage(1);
        return newCert;
    }, [onCountChange]);

    const openScanner = () => {
        // New certs land on page 1 unfiltered — make sure that's what's showing.
        if (appliedQuery) clearFilter();
        setPage(1);
        setScanning(true);
    };
    const closeScanner = useCallback(() => setScanning(false), []);

    const handleAddCert = async () => {
        const certNumber = newCertNumber.trim();
        if (!certNumber || addBusy) return;
        if (isOwned(certNumber)) {
            toast.error(`#${certNumber} is already in your certs.`);
            return;
        }
        setAddBusy(true);
        try {
            await addCert(certNumber);
            setNewCertNumber('');
            toast.success(`Added #${certNumber}`);
        } catch (e) {
            toast.error(`Failed to add #${certNumber}: ${e.message}`);
        } finally {
            setAddBusy(false);
        }
    };

    return (
        <div className="broker-tab-panel">
            <div className="broker-add-cert-row">
                <input
                    placeholder="Cert # to add"
                    value={newCertNumber}
                    onChange={(e) => setNewCertNumber(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddCert();
                        }
                    }}
                    disabled={addBusy}
                />
                <button
                    type="button"
                    className="broker-btn-primary"
                    onClick={handleAddCert}
                    disabled={addBusy || !newCertNumber.trim()}
                >
                    {addBusy ? 'Adding…' : 'Add'}
                </button>
                <button
                    type="button"
                    className="broker-btn-scan"
                    onClick={openScanner}
                    aria-label="Scan PSA certs with camera"
                    title="Scan with camera"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M4 8V6a2 2 0 012-2h2M16 4h2a2 2 0 012 2v2M20 16v2a2 2 0 01-2 2h-2M8 20H6a2 2 0 01-2-2v-2" />
                        <path d="M7 12h10" />
                    </svg>
                    Scan
                </button>
            </div>

            <div className="broker-toolbar">
                <input
                    placeholder="Filter by name / cert # / set…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            applyFilter();
                        }
                    }}
                />
                <button type="button" className="broker-btn-primary" onClick={applyFilter}>
                    Filter
                </button>
                {appliedQuery && (
                    <button type="button" className="broker-btn-ghost" onClick={clearFilter}>
                        Clear
                    </button>
                )}
            </div>

            {error && <p className="broker-error">{error}</p>}
            {loading && <p className="broker-loading">Loading certs…</p>}

            {!loading && !error && (
                pageCerts.length === 0 ? (
                    <p className="broker-empty">
                        {certs.length === 0
                            ? 'No certs yet — add one above to get started.'
                            : 'No certs match this filter.'}
                    </p>
                ) : (
                    <div className="broker-table-wrap">
                        <table className="broker-table">
                            <thead>
                                <tr>
                                    <th className="col-image"></th>
                                    <th>Cert #</th>
                                    <th>Name</th>
                                    <th className="col-grade">Grade</th>
                                    <th>Set / #</th>
                                    <th className="col-price">PriceCharting</th>
                                    <th>Product</th>
                                    <th className="col-price-input">Your Price</th>
                                    <th className="col-auto-price" title="Reprice automatically when market pricing updates">Auto<AutoPriceInfo /></th>
                                    <th className="col-actions"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageCerts.map((cert) => {
                                    const busy = !!busyRows[cert.cert_number];
                                    return (
                                        <tr
                                            key={cert.cert_number}
                                            className={[busy && 'busy', freshRows[String(cert.cert_number)] && 'cert-scanner-fresh'].filter(Boolean).join(' ')}
                                        >
                                            <td className="col-image">
                                                <CertImage cert={cert} onZoom={onZoom} />
                                            </td>
                                            <td className="cell-cert-number">
                                                <a
                                                    href={`https://www.psacard.com/cert/${cert.cert_number}/psa`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="broker-link"
                                                    title="View on PSA"
                                                >
                                                    {cert.cert_number}
                                                </a>
                                            </td>
                                            <td>{cert.name || '—'}</td>
                                            <td className="col-grade">{cert.grade ? cert.grade.match(/\d+/)?.[0] : '—'}</td>
                                            <td>
                                                <div className="cell-set">{cert.card?.set?.name || '—'}</div>
                                                <div className="cell-set-num muted">#{cert.set_number || '—'}</div>
                                            </td>
                                            <td><PriceChartingGradesCell cert={cert} /></td>
                                            <td><PriceChartingProductCell cert={cert} /></td>
                                            <td className="col-price-input">
                                                <div className="price-input-wrap">
                                                    <span className="price-input-prefix">$</span>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        className="price-input"
                                                        value={rowPrices[cert.cert_number] ?? ''}
                                                        onChange={(e) => onPriceChange(cert.cert_number, e.target.value)}
                                                        disabled={busy}
                                                    />
                                                </div>
                                            </td>
                                            <td className="col-auto-price">
                                                <input
                                                    type="checkbox"
                                                    aria-label={`Auto-price #${cert.cert_number}`}
                                                    checked={!!rowAutoPrice[cert.cert_number]}
                                                    onChange={(e) => onAutoPriceChange(cert.cert_number, e.target.checked)}
                                                    disabled={busy}
                                                />
                                            </td>
                                            <td className="col-actions">
                                                <button
                                                    type="button"
                                                    className="broker-btn-primary broker-btn-sm"
                                                    disabled={busy}
                                                    onClick={() => handleList(cert)}
                                                >
                                                    List
                                                </button>
                                                <button
                                                    type="button"
                                                    className="broker-btn-ghost broker-btn-sm"
                                                    disabled={busy}
                                                    onClick={() => handleHide(cert)}
                                                >
                                                    Hide
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )
            )}

            {!loading && !error && totalPages > 1 && (
                <div className="broker-pager">
                    <button
                        type="button"
                        className="broker-btn-ghost broker-btn-sm"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                        Prev
                    </button>
                    <span className="broker-pager-label">Page {page} of {totalPages}</span>
                    <button
                        type="button"
                        className="broker-btn-ghost broker-btn-sm"
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                        Next
                    </button>
                </div>
            )}

            <CertScanner
                open={scanning}
                onClose={closeScanner}
                addCert={addCert}
                isOwned={isOwned}
                getCert={getCert}
                listCert={listCert}
                updateListing={updateListing}
            />
        </div>
    );
};

export default CertsTab;
